/**
 * Currency detection and resolution utilities for multi-currency checkout.
 */

export interface CurrencyInfo {
  code: string;
  flag: string;
  symbol: string;
  label: string;
}

export const CURRENCY_CONFIG: Record<string, CurrencyInfo> = {
  eur: { code: "eur", flag: "\u{1F1EA}\u{1F1FA}", symbol: "\u20AC", label: "EUR" },
  usd: { code: "usd", flag: "\u{1F1FA}\u{1F1F8}", symbol: "$", label: "USD" },
  gbp: { code: "gbp", flag: "\u{1F1EC}\u{1F1E7}", symbol: "\u00A3", label: "GBP" },
  chf: { code: "chf", flag: "\u{1F1E8}\u{1F1ED}", symbol: "CHF", label: "CHF" },
  cad: { code: "cad", flag: "\u{1F1E8}\u{1F1E6}", symbol: "CA$", label: "CAD" },
  brl: { code: "brl", flag: "\u{1F1E7}\u{1F1F7}", symbol: "R$", label: "BRL" },
  mxn: { code: "mxn", flag: "\u{1F1F2}\u{1F1FD}", symbol: "MX$", label: "MXN" },
  aud: { code: "aud", flag: "\u{1F1E6}\u{1F1FA}", symbol: "A$", label: "AUD" },
};

// Locale → currency mapping
const LOCALE_CURRENCY_MAP: Record<string, string> = {
  "en-us": "usd",
  "en-gb": "gbp",
  "en-au": "aud",
  "en-ca": "cad",
  "fr-fr": "eur",
  "fr-be": "eur",
  "fr-ch": "chf",
  "fr-ca": "cad",
  "de-de": "eur",
  "de-at": "eur",
  "de-ch": "chf",
  "es-es": "eur",
  "es-mx": "mxn",
  "pt-br": "brl",
  "pt-pt": "eur",
  "it-it": "eur",
  "it-ch": "chf",
};

/**
 * Detect the user's preferred currency from browser locale.
 */
export function detectPreferredCurrency(): string | null {
  try {
    // Try navigator.language first
    const lang = navigator.language?.toLowerCase();
    if (lang && LOCALE_CURRENCY_MAP[lang]) {
      return LOCALE_CURRENCY_MAP[lang];
    }

    // Try Intl.DateTimeFormat resolved locale
    const resolved = Intl.DateTimeFormat().resolvedOptions().locale?.toLowerCase();
    if (resolved && LOCALE_CURRENCY_MAP[resolved]) {
      return LOCALE_CURRENCY_MAP[resolved];
    }

    // Fallback: try just the language part (e.g. "en" → "usd")
    const langShort = lang?.split("-")[0];
    if (langShort === "en") return "usd";
    if (langShort === "fr") return "eur";
    if (langShort === "de") return "eur";
    if (langShort === "es") return "eur";
    if (langShort === "pt") return "eur";
    if (langShort === "it") return "eur";

    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve the best currency to display at checkout.
 * Priority: sessionStorage > URL param > detected > default
 */
export function resolveCheckoutCurrency(
  availableCurrencies: string[],
  defaultCurrency: string,
  preferred: string | null,
): string {
  if (availableCurrencies.length <= 1) {
    return defaultCurrency;
  }

  // Check sessionStorage first (for 3DS redirect recovery)
  try {
    const stored = sessionStorage.getItem("gymkee_checkout_currency");
    if (stored && availableCurrencies.includes(stored)) {
      return stored;
    }
  } catch {
    // sessionStorage not available
  }

  // Check URL param
  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get("currency")?.toLowerCase();
  if (urlCurrency && availableCurrencies.includes(urlCurrency)) {
    return urlCurrency;
  }

  // Use preferred (detected from browser)
  if (preferred && availableCurrencies.includes(preferred)) {
    return preferred;
  }

  // Fallback to default
  return defaultCurrency;
}
